/**
 * AdminButton — the admin portal's button family, matching /brand
 * Aspect 11 (Button family). Variants: filled / outline / destructive /
 * ghost. Web hover + press feedback so every admin button reacts the
 * same way the rest of the app's buttons do. Theme-driven — no hex.
 */
import type { ReactNode } from "react";
import {
  ActivityIndicator,
  Platform,
  Pressable,
  Text,
  View,
  type ViewStyle,
} from "react-native";

import { COLORS, RADII, STROKES } from "../../../lib/brand";
import type { PaneTheme } from "../../brand/paneThemes";

export type AdminButtonVariant =
  | "filled"
  | "outline"
  | "destructive"
  | "ghost";
export type AdminButtonSize = "md" | "sm";

export type AdminButtonProps = {
  theme: PaneTheme;
  label: string;
  onPress: () => void;
  variant?: AdminButtonVariant;
  size?: AdminButtonSize;
  disabled?: boolean;
  loading?: boolean;
  /** Optional leading element (e.g. a small icon). */
  leading?: ReactNode;
  /** Stretch to fill the parent row. */
  fullWidth?: boolean;
  accessibilityLabel?: string;
};

export function AdminButton({
  theme,
  label,
  onPress,
  variant = "filled",
  size = "md",
  disabled = false,
  loading = false,
  leading,
  fullWidth = false,
  accessibilityLabel,
}: AdminButtonProps) {
  const isWeb = Platform.OS === "web";
  const inert = disabled || loading;

  const padV = size === "sm" ? 7 : 10;
  const padH = size === "sm" ? 12 : 14;
  const fontSize = size === "sm" ? 10.5 : 11.5;

  const visual = (hovered: boolean, pressed: boolean) => {
    const active = hovered || pressed;
    switch (variant) {
      case "filled":
        return {
          bg: pressed
            ? theme.accentDeep
            : hovered
              ? COLORS.AMBER_WARM.hex
              : theme.accent,
          border: "transparent",
          fg: theme.accentText,
        };
      case "outline":
        return {
          bg: active ? theme.accentSoft : "transparent",
          border: active ? theme.accentBorderHover : theme.borderStrong,
          fg: theme.accent,
        };
      case "destructive":
        return {
          bg: active ? theme.accentSoft : "transparent",
          border: theme.destructive,
          fg: theme.destructive,
        };
      case "ghost":
      default:
        return {
          bg: active ? theme.neutralHoverBgOnDark : "transparent",
          border: "transparent",
          fg: theme.textOnSurfaceDim,
        };
    }
  };

  return (
    <Pressable
      onPress={inert ? undefined : onPress}
      disabled={inert}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? label}
      accessibilityState={{ disabled: inert, busy: loading }}
      style={({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => {
        const v = visual(!!hovered && !inert, !!pressed && !inert);
        return [
          {
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: 7,
            alignSelf: fullWidth ? "stretch" : "flex-start",
            paddingVertical: padV,
            paddingHorizontal: padH,
            borderRadius: RADII.sm,
            borderWidth: STROKES.hairline,
            borderColor: v.border,
            backgroundColor: v.bg,
            opacity: inert ? 0.5 : 1,
          },
          isWeb
            ? ({
                cursor: inert ? "default" : "pointer",
                transition: "background-color 0.16s ease, border-color 0.16s ease",
              } as unknown as ViewStyle)
            : null,
        ];
      }}
    >
      {({ hovered, pressed }: { hovered?: boolean; pressed?: boolean }) => {
        const v = visual(!!hovered && !inert, !!pressed && !inert);
        return (
          <>
            {loading ? (
              <ActivityIndicator size="small" color={v.fg} />
            ) : leading != null ? (
              <View>{leading}</View>
            ) : null}
            <Text
              style={{
                fontFamily: theme.bodyBoldFont,
                fontSize,
                letterSpacing: 1.6,
                textTransform: "uppercase",
                color: v.fg,
              }}
            >
              {label}
            </Text>
          </>
        );
      }}
    </Pressable>
  );
}
